import { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SectionSidebarNav, type SectionSidebarNavItem } from "@/components/SectionSidebarNav";
import { Spinner } from "@/components/ui/Spinner";
import type { NavItem } from "@/lib/nav";
import { isPersonalSectionPath, usePersonalNavigation } from "@/lib/personalNav";
import {
  PersonalSubNavProvider,
  usePersonalSubNavRegistration,
  type PersonalSubNavRegistration,
} from "@/pages/personal/PersonalSubNavContext";

/**
 * Personal-Bereich mit linker Navigation.
 * 3. Ebene (z. B. Erfassen/Abwesenheit) erscheint als Unterpunkte im selben Menü,
 * nicht als zweite vertikale Sidebar.
 */
export function PersonalLayout() {
  const location = useLocation();
  const { visibleItems, showHub, loading } = usePersonalNavigation();
  const showSidebar = showHub && isPersonalSectionPath(location.pathname);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!showSidebar) {
    return <Outlet />;
  }

  return (
    <PersonalSubNavProvider>
      <PersonalLayoutWithNav items={visibleItems} pathname={location.pathname} />
    </PersonalSubNavProvider>
  );
}

function PersonalLayoutWithNav({
  items,
  pathname,
}: {
  items: NavItem[];
  pathname: string;
}) {
  const registration = usePersonalSubNavRegistration();
  const navItems = useMemo(
    () => buildPersonalNavItems(items, pathname, registration),
    [items, pathname, registration],
  );

  return (
    <div className="eh-bestellwesen-page-shell">
      <SectionSidebarNav items={navItems} ariaLabel="Personal" />
      <div className="eh-project-page-main">
        <Outlet />
      </div>
    </div>
  );
}

function buildPersonalNavItems(
  items: NavItem[],
  pathname: string,
  registration: PersonalSubNavRegistration | null,
): SectionSidebarNavItem[] {
  return items.map((item) => {
    const active = locationMatches(item, pathname);
    const children =
      registration?.parentKey === item.key ? registration.items : undefined;
    const external = item.internal === false || item.href.startsWith("/alt/");

    return {
      key: item.key,
      label: item.label,
      href: item.href,
      external,
      active,
      children,
      childrenOpen: Boolean(children?.length && active),
    };
  });
}

function locationMatches(item: NavItem, pathname: string): boolean {
  const path = pathname.replace(/\/$/, "") || "/";
  const href = item.href.replace(/\/$/, "") || "/";
  if (item.key === "zeiterfassung") {
    return path.startsWith("/zeiterfassung") || path.startsWith("/admin/zeiterfassung");
  }
  if (item.key === "mitarbeiter") {
    return path.startsWith("/admin/mitarbeiter");
  }
  if (item.key === "mein-bereich") {
    return path.startsWith("/personal/me");
  }
  if (item.key === "personalkosten") {
    return path.startsWith("/personal/personalkosten");
  }
  return path === href || path.startsWith(`${href}/`);
}
